import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Advertisement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/advertisement.types';
import {HighTierCouponBanner as HighTierCouponBannerType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackBASMonitoring, TrackPromotions} from '@aboutyou/tracking';
import {
  BannerImage,
  InfoIconWithLink,
  UnstyledLink,
  mediaTabletAndUp,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import React, {FC, useEffect} from 'react';
import styled from 'styled-components';
import {convertAdvertisementToTile} from '../../shared-deprecated/convertAdvertisementToTile';
import {useABTestAYOBasBanner} from '../AyOutletAbTest/useABTestAYOBasBanner';
import {CouponBanner} from '../Coupons/CouponBanner/CouponBanner';

interface HeaderBannerProps {
  highTierCouponBanner?: HighTierCouponBannerType;
  advertisement?: Advertisement;
  trackingKey: string;
  /**
   * Custom aspect ratio that may vary between pages, it should be a string to be interpreted by CSS correctly
   */
  aspectRatio?: string;
}

export const HeaderBanner: FC<HeaderBannerProps> = props => {
  const {isActive} = useIsFeatureActive('high_tier_coupons');
  return (
    <>
      <AdvertisementBanner {...props} />
      {props.highTierCouponBanner && isActive && (
        <CouponBanner banner={props.highTierCouponBanner} />
      )}
    </>
  );
};

const AdvertisementBanner: FC<HeaderBannerProps> = React.memo(
  function HeaderBannerView({advertisement, trackingKey, aspectRatio}) {
    const {shouldHideBasBanner, activateABTestAYOBasBanner, isAYOBasBanner} =
      useABTestAYOBasBanner();
    const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

    useEffect(() => {
      if (isAYOBasBanner(advertisement?.targetUrl?.url)) {
        activateABTestAYOBasBanner();
      }
    }, [activateABTestAYOBasBanner, isAYOBasBanner, advertisement]);

    if (!advertisement) {
      return (
        <TrackBASMonitoring
          basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
        />
      );
    }

    const advertisementTile = convertAdvertisementToTile(advertisement);
    const image = advertisement.image;

    if (advertisementTile === null || !image) {
      return (
        <TrackBASMonitoring
          basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
        />
      );
    }

    const {targetUrl, promotionTracking} = advertisementTile;

    if (isAYOBasBanner(targetUrl) && shouldHideBasBanner) {
      return (
        <TrackBASMonitoring
          basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
        />
      );
    }

    const bannerImage = (
      <StyledBannerImage
        $aspectRatio={aspectRatio}
        testId="BannerImage"
        url={image.url}
        disableFixedRatio
      />
    );

    return (
      <StyledTrackPromotions
        key={trackingKey}
        promotion={{
          advertisement: {
            promotionId: promotionTracking?.id ?? '',
            id: advertisementTile.id,
            path: targetUrl || '',
            name: advertisement.promotionTracking?.name || image.description,
          },
          source: TrackingPromotionCreative.BasCategoryHeader,
        }}
        streamPosition={0}
        threshold={0.5}
        triggerOnce
      >
        {targetUrl === undefined ? (
          bannerImage
        ) : (
          <UnstyledLink
            to={targetUrl || ''}
            aria-label={getAriaLabelFromUrl(targetUrl)}
          >
            {bannerImage}
          </UnstyledLink>
        )}
        {advertisement.infoIcon?.cta?.linkTarget.url && (
          <InfoIconWithLink
            to={advertisement.infoIcon.cta.linkTarget.url}
            aria-label={getAriaLabelFromUrl(
              advertisement.infoIcon.cta.linkTarget.url,
            )}
          />
        )}
      </StyledTrackPromotions>
    );
  },
);

const StyledBannerImage = styled(BannerImage)<{$aspectRatio?: string}>`
  aspect-ratio: 4 / 1.2; // The BAS team usually sets 2400x717 images

  @media ${mediaTabletAndUp} {
    margin-bottom: 1.2rem;
    margin-top: 2.375rem;
    aspect-ratio: ${({$aspectRatio}) => $aspectRatio ?? 5 / 1};
  }
`;

const StyledTrackPromotions = styled(TrackPromotions)`
  position: relative;
`;
